import type { Bit, ISO8601Date, Locale } from './common'
import type { HiveWordData } from './hive'
import type { GameplayWordToSave, SpellingList, UpdateCompetenceResponse } from './lists'
import type { DictionaryInfo, UserModel } from '.'

export interface SpellingHistoryInfo {
  timestamp: ISO8601Date
  percentage_score: number
  results: {
    dictionary_id: number | null
    correct: Bit
    word: string
  }[]
}

export interface SpellingHistoryListInfo {
  times_played: number
  id: number
  title: string
  s_code: string
}
export interface NumberGame {
  id: number
  timestamp: ISO8601Date
  user_id: number
  key: string
  score: number
  correct_count: number
  total_count: number
  duration: number
  difficulty: number
  earnings: number
  type: NumberGameType
  percentage_score: number
  results: readonly NumberGameAnswer[]
}

export interface NumberGameAnswer {
  id: number
  number_game_id: number
  question: string
  answer_given: string
  correct: Bit
  time: number
}

export interface HiveGameInfo {
  id: number
  room: number | null
  creator_id: number
  created: ISO8601Date
  expiry: ISO8601Date
  status: string
  list_id: number
  list_ident: string
  phonics: number
  words: string
  words_ids: HiveWordData[]
  difficulty: number
  show_words: number
  shuffle: number
  auto_play: number
  hub_audio: number
  show_leagues: number
  locale: string
  list_title: string
  creator_username: string
  teacher: number
  wordsData?: GameSessionWordData[]
  assignmentId?: number
  assignmentTitle?: string
  assignmentIsTest?: boolean
}

export interface HiveGameData {
  id: number
  room: number
  creator_id: number
  created: ISO8601Date
  expiry: ISO8601Date
  status: string
  list_id: number
  list_ident: string
  phonics: number
  words: string
  difficulty: number
  show_words: number
  shuffle: number
  auto_play: number
  hub_audio: number
  show_leagues: number
  locale: string
  data: any[]
  league: any[]
}

export interface AdaptiveSpellingGameSessionWithoutWordData {
  id: number
  locale: Locale
  phonics: number
  gameType?: SpellingGameType
  total_words?: number
  earnedReward?: boolean
}

export interface AdaptiveSpellingGameSession extends AdaptiveSpellingGameSessionWithoutWordData {
  currentWordData: AdaptiveGameSessionWordData
}

export interface MiniDiagnosticGameSession extends AdaptiveSpellingGameSessionWithoutWordData {
  currentWordData: GameSessionWordData
}

export type MiniDiagnosticGameSessionOrFinished = { session: MiniDiagnosticGameSession } | { finished: true }

export interface GameSession {
  id: number
  list: string
  difficulty: number
  wordsData: GameSessionWordData[]
  words: string[]
  locale: Locale
  phonics: number
  gameType?: SpellingGameType
  hive?: number
  hiveId?: number
  hubAudio?: number
  showLeagues?: number
}

export interface TestModeGameSession {
  id: number
  list: string
  wordsData: GameSessionWordData[]
  words: string[]
  locale: Locale
  existingScore: number | null
  playedWordIndex: number | null
  resultsArray: boolean[] | null
  testComplete: boolean
  difficulty: number
  phonics: number
}

// read only - do not use in API
export interface SpellingGameSession extends GameSession {
  readonly existingScore?: number
  readonly playedWordIndex?: number
  readonly resultsArray?: boolean[]
  readonly testComplete?: boolean
  readonly isTest?: boolean
  readonly practiceForTestHomeworkId?: number
}

export type SpellingGameHiveSession = Partial<Pick<SpellingGameSession, 'id'>> & Omit<SpellingGameSession, 'id'>

export interface GameSessionWordData {
  ident: string
  text: string
  dictionary: DictionaryInfo
  difficulty?: number
  adaptiveLevel?: number
  diagnostic?: boolean
}

export interface AdaptiveGameSessionWordData extends GameSessionWordData {
  difficulty: number
  adaptiveLevel: number
  focusPoolStatus: AdaptiveSpellingWordFocusPoolStatus
}
export interface GameSessionUpdateParams {
  score: number
  words: GameplayWordToSave[]
  difficulty: number
  hiveId?: number
  isTest?: boolean
}

export interface LogGameplayResponse {
  status: 'Success'
  user?: UserModel
  competence: UpdateCompetenceResponse
  id: number
}

export const SpellingGameType = ['spelling', 'sentences', 'definitions', 'squirrel', 'adaptive spelling', 'adaptive spelling mini-diagnostic'] as const
export type SpellingGameType = typeof SpellingGameType[number]

export const NumberGameType = ['standard', 'squirrel'] as const
export type NumberGameType = typeof NumberGameType[number]

export interface NumberGameSession {
  score: number
  answers: NumberGameAnswerGiven[]
  key: string
  difficulty: number
  type?: NumberGameType
}

export interface NumberGameAnswerGiven {
  question: string
  answer: string
  correct: boolean
  time: number
  firstNumber?: number
  secondNumber?: number
  operation?: NumberGameOperationType
}

export const AdaptiveSpellingRegion = ['en_GB', 'en_US', 'en_ZA', 'en_AU'] as const
export type AdaptiveSpellingRegion = typeof AdaptiveSpellingRegion[number]

export type AdaptiveSpellingWordFocusPoolStatus = 'current' | 'future' | 'pending mastery' | 'mastered' | null

export interface AdaptiveSpellingUserWord {
  dictionaryId: number
  word: string
  currentLevel: number
  difficultyIndex?: number
}

export interface AdaptiveSpellingResultRequest {
  days: number | 'all'
  maxCount?: number
  onlyCorrect?: boolean
  onlyExtreme?: boolean
}

export interface AdaptiveSpellingResult {
  dictionaryId: number
  date: ISO8601Date
  word: string
  answer: string
  difficultyIndex: number | null
  playedLevel: number | null
  correct: boolean
}
export interface AdaptiveSpellingUserLevelHistory {
  date: ISO8601Date
  level: number
}

export interface AdaptiveSpellingUsersLevelDelta {
  current: AdaptiveSpellingUserLevelHistory
  previous: AdaptiveSpellingUserLevelHistory | null
}

export interface AdaptiveSpellingUserProgress {
  userId: number
  hasStartedMiniDiagnostic: boolean
  hasCompletedMiniDiagnostic: boolean
  hasPlayedAdaptiveSpelling: boolean
  currentStage: number | null
  rewardsPlayCount: number
  canEarnPlayCountToday: boolean
  hasRewardToRedeem: boolean
  roundsPlayed: number
  wordsPlayed: number
  wordsMastered: number
  bonusHoneypotsEarned: number
  levelDelta?: AdaptiveSpellingUsersLevelDelta
}

export interface SaveBeekeeperGameResponse {
  status: true
  user: UserModel
  competence: UpdateCompetenceResponse
}

export type WeeklyGamePlayUserActivity = {
  userId: number
  playedYesterday: boolean
}[]

export interface GameModePlayCount {
  count: number
  type: SpellingGameType
}

export interface NormalSpellingGameplaysOption {
  type: 'normal'
  stage: number
}

export interface MasteryZoneSpellingGameplaysOption {
  type: 'mastery-zone'
}

export type SpellingGameplaysOptions = NormalSpellingGameplaysOption | MasteryZoneSpellingGameplaysOption

interface CustomNumberFormatOptions extends Intl.NumberFormatOptions {
  numberingSystem?: string
}

export const localeStringOptions: CustomNumberFormatOptions = {
  numberingSystem: 'latn'
}

export function transformNumberQuestionToLocaleString (question: string | undefined): string {
  if (!question) {
    return ''
  }
  if (question === '...') { return '...' }
  const questionArray = question.split(' ')
  const nonDigitsSigns = ['+', '-', '=', '?', '÷', '×', ' ']
  return questionArray
    .map((i) => {
      if (nonDigitsSigns.includes(i)) {
        return i
      } else {
        return enforceLatinNumberSystem(parseFloat(i.replace(',', '')))
      }
    })
    .join(' ')
}

export function enforceLatinNumberSystem (number: number): string {
  if (typeof number !== 'number') {
    return ''
  }
  return number.toLocaleString(undefined, localeStringOptions)
}

// Time Table Mastery
export interface TimesTablesCompetencyOptions {
  pupils: number[]
  tables: number[]
  operations: ('multiplication' | 'division')[]
}

export interface TimesTablesCompetencyData {
  userId: number
  factId: number
  question: string
  firstNumber: number
  secondNumber: number
  operation: FactOperationType
  corrects: number[]
  difficulties: number[]
  times: number[]
  avgCorrect: number
  avgTime: number
  avgDifficulty: number
  competency: number
  totalAttempts: number
}

// accuracy, fluency, difficulty
export const competencyLookup = {
  '1,1,1': 1,
  '1,1,2': 1,
  '1,1,3': 1,
  '1,2,1': 1,
  '1,2,2': 1,
  '1,2,3': 1,
  '1,3,1': 1,
  '1,3,2': 1,
  '1,3,3': 1,
  '1,4,1': 1,
  '1,4,2': 1,
  '1,4,3': 1,
  '1,1,4': 1,
  '1,2,4': 1,
  '1,3,4': 1,
  '1,4,4': 1,

  '2,1,1': 2,
  '2,1,2': 2,
  '2,1,3': 2,
  '2,2,1': 2,
  '2,2,2': 2,
  '2,2,3': 2,
  '2,3,1': 1,
  '2,3,2': 2,
  '2,3,3': 1,
  '2,4,1': 1,
  '2,4,2': 2,
  '2,4,3': 2,
  '2,1,4': 2,
  '2,2,4': 2,
  '2,3,4': 2,
  '2,4,4': 2,

  '3,1,1': 2,
  '3,1,2': 3,
  '3,1,3': 3,
  '3,2,1': 2,
  '3,2,2': 3,
  '3,2,3': 3,
  '3,3,1': 2,
  '3,3,2': 2,
  '3,3,3': 2,
  '3,4,1': 2,
  '3,4,2': 2,
  '3,4,3': 3,
  '3,1,4': 3,
  '3,2,4': 3,
  '3,3,4': 3,
  '3,4,4': 2,

  '4,1,1': 3,
  '4,1,2': 3,
  '4,1,3': 4,
  '4,2,1': 3,
  '4,2,2': 3,
  '4,2,3': 4,
  '4,3,1': 2,
  '4,3,2': 3,
  '4,3,3': 3,
  '4,4,1': 2,
  '4,4,2': 3,
  '4,4,3': 3,
  '4,1,4': 4,
  '4,2,4': 4,
  '4,3,4': 3,
  '4,4,4': 3,

  '5,1,1': 4,
  '5,1,2': 4,
  '5,1,3': 5,
  '5,2,1': 4,
  '5,2,2': 4,
  '5,2,3': 5,
  '5,3,1': 3,
  '5,3,2': 3,
  '5,3,3': 4,
  '5,4,1': 3,
  '5,4,2': 3,
  '5,4,3': 3,
  '5,1,4': 5,
  '5,2,4': 5,
  '5,3,4': 4,
  '5,4,4': 4
}

export function calculateAccuracy (avgCorrect: number) {
  if (avgCorrect >= 0.95) { return 5 }
  if (avgCorrect >= 0.90) { return 4 }
  if (avgCorrect >= 0.85) { return 3 }
  if (avgCorrect >= 0.75) { return 2 }
  return 1
}

export function calculateFluency (avgTime: number) {
  if (avgTime >= 4) { return 4 }
  if (avgTime >= 2) { return 3 }
  if (avgTime >= 1) { return 2 }
  return 1
}

export function calculateDifficulty (avgDifficulty: number) {
  if (avgDifficulty > 3) { return 4 }
  if (avgDifficulty > 2) { return 3 }
  if (avgDifficulty > 1) { return 2 }
  return 1
}

export const NumberGameOperationType = ['addition', 'subtraction', 'multiplication', 'division', 'numberBondsAddition', 'numberBondsSubtraction'] as const
export type NumberGameOperationType = typeof NumberGameOperationType[number]

export function getOperationType (array: string[]) {
  if (array.length === 5) {
    if (array.includes('-')) {
      return 'numberBondsSubtraction'
    } else {
      return 'numberBondsAddition'
    }
  } else if (array.includes('×')) {
    return 'multiplication'
  } else if (array.includes('÷')) {
    return 'division'
  } else if (array.includes('-')) {
    return 'subtraction'
  } else {
    return 'addition'
  }
}

export function parseNumberQuestion (question: string, key: string): { firstNumber: number, secondNumber: number, operation: NumberGameOperationType } {
  const question_array = question.split(' ')
  let firstNumber: number
  let secondNumber: number
  const operation = getOperationType(question_array)

  if (question_array.length === 3) {
    firstNumber = parseInt(question_array[0])
    secondNumber = parseInt(question_array[2])
  } else {
    firstNumber = parseInt(key.split('_')[1])
    secondNumber = parseInt(question_array.filter(q => q.match(/\d+/)).filter(q => q !== firstNumber.toString())[0]) || firstNumber
  }

  return { firstNumber, secondNumber, operation }
}

export function mean (arr: number[], attempts: number): number {
  const sum = arr.reduce((a, b) => a + b, 0)
  return sum / attempts
}

export const FactOperationType = ['multiplication', 'division'] as const
export type FactOperationType = typeof FactOperationType[number]

export interface TimesTableFact {
  id: number
  question: string
  firstNumber: number
  secondNumber: number
  operation: FactOperationType
}

export const timesTablesFacts: TimesTableFact[] = [
  { id: 1, question: '2 × 1', firstNumber: 2, secondNumber: 1, operation: 'multiplication' },
  { id: 2, question: '2 × 2', firstNumber: 2, secondNumber: 2, operation: 'multiplication' },
  { id: 3, question: '2 × 3', firstNumber: 2, secondNumber: 3, operation: 'multiplication' },
  { id: 4, question: '2 × 4', firstNumber: 2, secondNumber: 4, operation: 'multiplication' },
  { id: 5, question: '2 × 5', firstNumber: 2, secondNumber: 5, operation: 'multiplication' },
  { id: 6, question: '2 × 6', firstNumber: 2, secondNumber: 6, operation: 'multiplication' },
  { id: 7, question: '2 × 7', firstNumber: 2, secondNumber: 7, operation: 'multiplication' },
  { id: 8, question: '2 × 8', firstNumber: 2, secondNumber: 8, operation: 'multiplication' },
  { id: 9, question: '2 × 9', firstNumber: 2, secondNumber: 9, operation: 'multiplication' },
  { id: 10, question: '2 × 10', firstNumber: 2, secondNumber: 10, operation: 'multiplication' },
  { id: 11, question: '2 × 11', firstNumber: 2, secondNumber: 11, operation: 'multiplication' },
  { id: 12, question: '2 × 12', firstNumber: 2, secondNumber: 12, operation: 'multiplication' },
  { id: 13, question: '3 × 1', firstNumber: 3, secondNumber: 1, operation: 'multiplication' },
  { id: 14, question: '3 × 2', firstNumber: 3, secondNumber: 2, operation: 'multiplication' },
  { id: 15, question: '3 × 3', firstNumber: 3, secondNumber: 3, operation: 'multiplication' },
  { id: 16, question: '3 × 4', firstNumber: 3, secondNumber: 4, operation: 'multiplication' },
  { id: 17, question: '3 × 5', firstNumber: 3, secondNumber: 5, operation: 'multiplication' },
  { id: 18, question: '3 × 6', firstNumber: 3, secondNumber: 6, operation: 'multiplication' },
  { id: 19, question: '3 × 7', firstNumber: 3, secondNumber: 7, operation: 'multiplication' },
  { id: 20, question: '3 × 8', firstNumber: 3, secondNumber: 8, operation: 'multiplication' },
  { id: 21, question: '3 × 9', firstNumber: 3, secondNumber: 9, operation: 'multiplication' },
  { id: 22, question: '3 × 10', firstNumber: 3, secondNumber: 10, operation: 'multiplication' },
  { id: 23, question: '3 × 11', firstNumber: 3, secondNumber: 11, operation: 'multiplication' },
  { id: 24, question: '3 × 12', firstNumber: 3, secondNumber: 12, operation: 'multiplication' },
  { id: 25, question: '4 × 1', firstNumber: 4, secondNumber: 1, operation: 'multiplication' },
  { id: 26, question: '4 × 2', firstNumber: 4, secondNumber: 2, operation: 'multiplication' },
  { id: 27, question: '4 × 3', firstNumber: 4, secondNumber: 3, operation: 'multiplication' },
  { id: 28, question: '4 × 4', firstNumber: 4, secondNumber: 4, operation: 'multiplication' },
  { id: 29, question: '4 × 5', firstNumber: 4, secondNumber: 5, operation: 'multiplication' },
  { id: 30, question: '4 × 6', firstNumber: 4, secondNumber: 6, operation: 'multiplication' },
  { id: 31, question: '4 × 7', firstNumber: 4, secondNumber: 7, operation: 'multiplication' },
  { id: 32, question: '4 × 8', firstNumber: 4, secondNumber: 8, operation: 'multiplication' },
  { id: 33, question: '4 × 9', firstNumber: 4, secondNumber: 9, operation: 'multiplication' },
  { id: 34, question: '4 × 10', firstNumber: 4, secondNumber: 10, operation: 'multiplication' },
  { id: 35, question: '4 × 11', firstNumber: 4, secondNumber: 11, operation: 'multiplication' },
  { id: 36, question: '4 × 12', firstNumber: 4, secondNumber: 12, operation: 'multiplication' },
  { id: 37, question: '5 × 1', firstNumber: 5, secondNumber: 1, operation: 'multiplication' },
  { id: 38, question: '5 × 2', firstNumber: 5, secondNumber: 2, operation: 'multiplication' },
  { id: 39, question: '5 × 3', firstNumber: 5, secondNumber: 3, operation: 'multiplication' },
  { id: 40, question: '5 × 4', firstNumber: 5, secondNumber: 4, operation: 'multiplication' },
  { id: 41, question: '5 × 5', firstNumber: 5, secondNumber: 5, operation: 'multiplication' },
  { id: 42, question: '5 × 6', firstNumber: 5, secondNumber: 6, operation: 'multiplication' },
  { id: 43, question: '5 × 7', firstNumber: 5, secondNumber: 7, operation: 'multiplication' },
  { id: 44, question: '5 × 8', firstNumber: 5, secondNumber: 8, operation: 'multiplication' },
  { id: 45, question: '5 × 9', firstNumber: 5, secondNumber: 9, operation: 'multiplication' },
  { id: 46, question: '5 × 10', firstNumber: 5, secondNumber: 10, operation: 'multiplication' },
  { id: 47, question: '5 × 11', firstNumber: 5, secondNumber: 11, operation: 'multiplication' },
  { id: 48, question: '5 × 12', firstNumber: 5, secondNumber: 12, operation: 'multiplication' },
  { id: 49, question: '6 × 1', firstNumber: 6, secondNumber: 1, operation: 'multiplication' },
  { id: 50, question: '6 × 2', firstNumber: 6, secondNumber: 2, operation: 'multiplication' },
  { id: 51, question: '6 × 3', firstNumber: 6, secondNumber: 3, operation: 'multiplication' },
  { id: 52, question: '6 × 4', firstNumber: 6, secondNumber: 4, operation: 'multiplication' },
  { id: 53, question: '6 × 5', firstNumber: 6, secondNumber: 5, operation: 'multiplication' },
  { id: 54, question: '6 × 6', firstNumber: 6, secondNumber: 6, operation: 'multiplication' },
  { id: 55, question: '6 × 7', firstNumber: 6, secondNumber: 7, operation: 'multiplication' },
  { id: 56, question: '6 × 8', firstNumber: 6, secondNumber: 8, operation: 'multiplication' },
  { id: 57, question: '6 × 9', firstNumber: 6, secondNumber: 9, operation: 'multiplication' },
  { id: 58, question: '6 × 10', firstNumber: 6, secondNumber: 10, operation: 'multiplication' },
  { id: 59, question: '6 × 11', firstNumber: 6, secondNumber: 11, operation: 'multiplication' },
  { id: 60, question: '6 × 12', firstNumber: 6, secondNumber: 12, operation: 'multiplication' },
  { id: 61, question: '7 × 1', firstNumber: 7, secondNumber: 1, operation: 'multiplication' },
  { id: 62, question: '7 × 2', firstNumber: 7, secondNumber: 2, operation: 'multiplication' },
  { id: 63, question: '7 × 3', firstNumber: 7, secondNumber: 3, operation: 'multiplication' },
  { id: 64, question: '7 × 4', firstNumber: 7, secondNumber: 4, operation: 'multiplication' },
  { id: 65, question: '7 × 5', firstNumber: 7, secondNumber: 5, operation: 'multiplication' },
  { id: 66, question: '7 × 6', firstNumber: 7, secondNumber: 6, operation: 'multiplication' },
  { id: 67, question: '7 × 7', firstNumber: 7, secondNumber: 7, operation: 'multiplication' },
  { id: 68, question: '7 × 8', firstNumber: 7, secondNumber: 8, operation: 'multiplication' },
  { id: 69, question: '7 × 9', firstNumber: 7, secondNumber: 9, operation: 'multiplication' },
  { id: 70, question: '7 × 10', firstNumber: 7, secondNumber: 10, operation: 'multiplication' },
  { id: 71, question: '7 × 11', firstNumber: 7, secondNumber: 11, operation: 'multiplication' },
  { id: 72, question: '7 × 12', firstNumber: 7, secondNumber: 12, operation: 'multiplication' },
  { id: 73, question: '8 × 1', firstNumber: 8, secondNumber: 1, operation: 'multiplication' },
  { id: 74, question: '8 × 2', firstNumber: 8, secondNumber: 2, operation: 'multiplication' },
  { id: 75, question: '8 × 3', firstNumber: 8, secondNumber: 3, operation: 'multiplication' },
  { id: 76, question: '8 × 4', firstNumber: 8, secondNumber: 4, operation: 'multiplication' },
  { id: 77, question: '8 × 5', firstNumber: 8, secondNumber: 5, operation: 'multiplication' },
  { id: 78, question: '8 × 6', firstNumber: 8, secondNumber: 6, operation: 'multiplication' },
  { id: 79, question: '8 × 7', firstNumber: 8, secondNumber: 7, operation: 'multiplication' },
  { id: 80, question: '8 × 8', firstNumber: 8, secondNumber: 8, operation: 'multiplication' },
  { id: 81, question: '8 × 9', firstNumber: 8, secondNumber: 9, operation: 'multiplication' },
  { id: 82, question: '8 × 10', firstNumber: 8, secondNumber: 10, operation: 'multiplication' },
  { id: 83, question: '8 × 11', firstNumber: 8, secondNumber: 11, operation: 'multiplication' },
  { id: 84, question: '8 × 12', firstNumber: 8, secondNumber: 12, operation: 'multiplication' },
  { id: 85, question: '9 × 1', firstNumber: 9, secondNumber: 1, operation: 'multiplication' },
  { id: 86, question: '9 × 2', firstNumber: 9, secondNumber: 2, operation: 'multiplication' },
  { id: 87, question: '9 × 3', firstNumber: 9, secondNumber: 3, operation: 'multiplication' },
  { id: 88, question: '9 × 4', firstNumber: 9, secondNumber: 4, operation: 'multiplication' },
  { id: 89, question: '9 × 5', firstNumber: 9, secondNumber: 5, operation: 'multiplication' },
  { id: 90, question: '9 × 6', firstNumber: 9, secondNumber: 6, operation: 'multiplication' },
  { id: 91, question: '9 × 7', firstNumber: 9, secondNumber: 7, operation: 'multiplication' },
  { id: 92, question: '9 × 8', firstNumber: 9, secondNumber: 8, operation: 'multiplication' },
  { id: 93, question: '9 × 9', firstNumber: 9, secondNumber: 9, operation: 'multiplication' },
  { id: 94, question: '9 × 10', firstNumber: 9, secondNumber: 10, operation: 'multiplication' },
  { id: 95, question: '9 × 11', firstNumber: 9, secondNumber: 11, operation: 'multiplication' },
  { id: 96, question: '9 × 12', firstNumber: 9, secondNumber: 12, operation: 'multiplication' },
  { id: 97, question: '10 × 1', firstNumber: 10, secondNumber: 1, operation: 'multiplication' },
  { id: 98, question: '10 × 2', firstNumber: 10, secondNumber: 2, operation: 'multiplication' },
  { id: 99, question: '10 × 3', firstNumber: 10, secondNumber: 3, operation: 'multiplication' },
  { id: 100, question: '10 × 4', firstNumber: 10, secondNumber: 4, operation: 'multiplication' },
  { id: 101, question: '10 × 5', firstNumber: 10, secondNumber: 5, operation: 'multiplication' },
  { id: 102, question: '10 × 6', firstNumber: 10, secondNumber: 6, operation: 'multiplication' },
  { id: 103, question: '10 × 7', firstNumber: 10, secondNumber: 7, operation: 'multiplication' },
  { id: 104, question: '10 × 8', firstNumber: 10, secondNumber: 8, operation: 'multiplication' },
  { id: 105, question: '10 × 9', firstNumber: 10, secondNumber: 9, operation: 'multiplication' },
  { id: 106, question: '10 × 10', firstNumber: 10, secondNumber: 10, operation: 'multiplication' },
  { id: 107, question: '10 × 11', firstNumber: 10, secondNumber: 11, operation: 'multiplication' },
  { id: 108, question: '10 × 12', firstNumber: 10, secondNumber: 12, operation: 'multiplication' },
  { id: 109, question: '11 × 1', firstNumber: 11, secondNumber: 1, operation: 'multiplication' },
  { id: 110, question: '11 × 2', firstNumber: 11, secondNumber: 2, operation: 'multiplication' },
  { id: 111, question: '11 × 3', firstNumber: 11, secondNumber: 3, operation: 'multiplication' },
  { id: 112, question: '11 × 4', firstNumber: 11, secondNumber: 4, operation: 'multiplication' },
  { id: 113, question: '11 × 5', firstNumber: 11, secondNumber: 5, operation: 'multiplication' },
  { id: 114, question: '11 × 6', firstNumber: 11, secondNumber: 6, operation: 'multiplication' },
  { id: 115, question: '11 × 7', firstNumber: 11, secondNumber: 7, operation: 'multiplication' },
  { id: 116, question: '11 × 8', firstNumber: 11, secondNumber: 8, operation: 'multiplication' },
  { id: 117, question: '11 × 9', firstNumber: 11, secondNumber: 9, operation: 'multiplication' },
  { id: 118, question: '11 × 10', firstNumber: 11, secondNumber: 10, operation: 'multiplication' },
  { id: 119, question: '11 × 11', firstNumber: 11, secondNumber: 11, operation: 'multiplication' },
  { id: 120, question: '11 × 12', firstNumber: 11, secondNumber: 12, operation: 'multiplication' },
  { id: 121, question: '12 × 1', firstNumber: 12, secondNumber: 1, operation: 'multiplication' },
  { id: 122, question: '12 × 2', firstNumber: 12, secondNumber: 2, operation: 'multiplication' },
  { id: 123, question: '12 × 3', firstNumber: 12, secondNumber: 3, operation: 'multiplication' },
  { id: 124, question: '12 × 4', firstNumber: 12, secondNumber: 4, operation: 'multiplication' },
  { id: 125, question: '12 × 5', firstNumber: 12, secondNumber: 5, operation: 'multiplication' },
  { id: 126, question: '12 × 6', firstNumber: 12, secondNumber: 6, operation: 'multiplication' },
  { id: 127, question: '12 × 7', firstNumber: 12, secondNumber: 7, operation: 'multiplication' },
  { id: 128, question: '12 × 8', firstNumber: 12, secondNumber: 8, operation: 'multiplication' },
  { id: 129, question: '12 × 9', firstNumber: 12, secondNumber: 9, operation: 'multiplication' },
  { id: 130, question: '12 × 10', firstNumber: 12, secondNumber: 10, operation: 'multiplication' },
  { id: 131, question: '12 × 11', firstNumber: 12, secondNumber: 11, operation: 'multiplication' },
  { id: 132, question: '12 × 12', firstNumber: 12, secondNumber: 12, operation: 'multiplication' },
  { id: 133, question: '2 ÷ 2', firstNumber: 2, secondNumber: 2, operation: 'division' },
  { id: 134, question: '4 ÷ 2', firstNumber: 4, secondNumber: 2, operation: 'division' },
  { id: 135, question: '6 ÷ 2', firstNumber: 6, secondNumber: 2, operation: 'division' },
  { id: 136, question: '8 ÷ 2', firstNumber: 8, secondNumber: 2, operation: 'division' },
  { id: 137, question: '10 ÷ 2', firstNumber: 10, secondNumber: 2, operation: 'division' },
  { id: 138, question: '12 ÷ 2', firstNumber: 12, secondNumber: 2, operation: 'division' },
  { id: 139, question: '14 ÷ 2', firstNumber: 14, secondNumber: 2, operation: 'division' },
  { id: 140, question: '16 ÷ 2', firstNumber: 16, secondNumber: 2, operation: 'division' },
  { id: 141, question: '18 ÷ 2', firstNumber: 18, secondNumber: 2, operation: 'division' },
  { id: 142, question: '20 ÷ 2', firstNumber: 20, secondNumber: 2, operation: 'division' },
  { id: 143, question: '22 ÷ 2', firstNumber: 22, secondNumber: 2, operation: 'division' },
  { id: 144, question: '24 ÷ 2', firstNumber: 24, secondNumber: 2, operation: 'division' },
  { id: 145, question: '3 ÷ 3', firstNumber: 3, secondNumber: 3, operation: 'division' },
  { id: 146, question: '6 ÷ 3', firstNumber: 6, secondNumber: 3, operation: 'division' },
  { id: 147, question: '9 ÷ 3', firstNumber: 9, secondNumber: 3, operation: 'division' },
  { id: 148, question: '12 ÷ 3', firstNumber: 12, secondNumber: 3, operation: 'division' },
  { id: 149, question: '15 ÷ 3', firstNumber: 15, secondNumber: 3, operation: 'division' },
  { id: 150, question: '18 ÷ 3', firstNumber: 18, secondNumber: 3, operation: 'division' },
  { id: 151, question: '21 ÷ 3', firstNumber: 21, secondNumber: 3, operation: 'division' },
  { id: 152, question: '24 ÷ 3', firstNumber: 24, secondNumber: 3, operation: 'division' },
  { id: 153, question: '27 ÷ 3', firstNumber: 27, secondNumber: 3, operation: 'division' },
  { id: 154, question: '30 ÷ 3', firstNumber: 30, secondNumber: 3, operation: 'division' },
  { id: 155, question: '33 ÷ 3', firstNumber: 33, secondNumber: 3, operation: 'division' },
  { id: 156, question: '36 ÷ 3', firstNumber: 36, secondNumber: 3, operation: 'division' },
  { id: 157, question: '4 ÷ 4', firstNumber: 4, secondNumber: 4, operation: 'division' },
  { id: 158, question: '8 ÷ 4', firstNumber: 8, secondNumber: 4, operation: 'division' },
  { id: 159, question: '12 ÷ 4', firstNumber: 12, secondNumber: 4, operation: 'division' },
  { id: 160, question: '16 ÷ 4', firstNumber: 16, secondNumber: 4, operation: 'division' },
  { id: 161, question: '20 ÷ 4', firstNumber: 20, secondNumber: 4, operation: 'division' },
  { id: 162, question: '24 ÷ 4', firstNumber: 24, secondNumber: 4, operation: 'division' },
  { id: 163, question: '28 ÷ 4', firstNumber: 28, secondNumber: 4, operation: 'division' },
  { id: 164, question: '32 ÷ 4', firstNumber: 32, secondNumber: 4, operation: 'division' },
  { id: 165, question: '36 ÷ 4', firstNumber: 36, secondNumber: 4, operation: 'division' },
  { id: 166, question: '40 ÷ 4', firstNumber: 40, secondNumber: 4, operation: 'division' },
  { id: 167, question: '44 ÷ 4', firstNumber: 44, secondNumber: 4, operation: 'division' },
  { id: 168, question: '48 ÷ 4', firstNumber: 48, secondNumber: 4, operation: 'division' },
  { id: 169, question: '5 ÷ 5', firstNumber: 5, secondNumber: 5, operation: 'division' },
  { id: 170, question: '10 ÷ 5', firstNumber: 10, secondNumber: 5, operation: 'division' },
  { id: 171, question: '15 ÷ 5', firstNumber: 15, secondNumber: 5, operation: 'division' },
  { id: 172, question: '20 ÷ 5', firstNumber: 20, secondNumber: 5, operation: 'division' },
  { id: 173, question: '25 ÷ 5', firstNumber: 25, secondNumber: 5, operation: 'division' },
  { id: 174, question: '30 ÷ 5', firstNumber: 30, secondNumber: 5, operation: 'division' },
  { id: 175, question: '35 ÷ 5', firstNumber: 35, secondNumber: 5, operation: 'division' },
  { id: 176, question: '40 ÷ 5', firstNumber: 40, secondNumber: 5, operation: 'division' },
  { id: 177, question: '45 ÷ 5', firstNumber: 45, secondNumber: 5, operation: 'division' },
  { id: 178, question: '50 ÷ 5', firstNumber: 50, secondNumber: 5, operation: 'division' },
  { id: 179, question: '55 ÷ 5', firstNumber: 55, secondNumber: 5, operation: 'division' },
  { id: 180, question: '60 ÷ 5', firstNumber: 60, secondNumber: 5, operation: 'division' },
  { id: 181, question: '6 ÷ 6', firstNumber: 6, secondNumber: 6, operation: 'division' },
  { id: 182, question: '12 ÷ 6', firstNumber: 12, secondNumber: 6, operation: 'division' },
  { id: 183, question: '18 ÷ 6', firstNumber: 18, secondNumber: 6, operation: 'division' },
  { id: 184, question: '24 ÷ 6', firstNumber: 24, secondNumber: 6, operation: 'division' },
  { id: 185, question: '30 ÷ 6', firstNumber: 30, secondNumber: 6, operation: 'division' },
  { id: 186, question: '36 ÷ 6', firstNumber: 36, secondNumber: 6, operation: 'division' },
  { id: 187, question: '42 ÷ 6', firstNumber: 42, secondNumber: 6, operation: 'division' },
  { id: 188, question: '48 ÷ 6', firstNumber: 48, secondNumber: 6, operation: 'division' },
  { id: 189, question: '54 ÷ 6', firstNumber: 54, secondNumber: 6, operation: 'division' },
  { id: 190, question: '60 ÷ 6', firstNumber: 60, secondNumber: 6, operation: 'division' },
  { id: 191, question: '66 ÷ 6', firstNumber: 66, secondNumber: 6, operation: 'division' },
  { id: 192, question: '72 ÷ 6', firstNumber: 72, secondNumber: 6, operation: 'division' },
  { id: 193, question: '7 ÷ 7', firstNumber: 7, secondNumber: 7, operation: 'division' },
  { id: 194, question: '14 ÷ 7', firstNumber: 14, secondNumber: 7, operation: 'division' },
  { id: 195, question: '21 ÷ 7', firstNumber: 21, secondNumber: 7, operation: 'division' },
  { id: 196, question: '28 ÷ 7', firstNumber: 28, secondNumber: 7, operation: 'division' },
  { id: 197, question: '35 ÷ 7', firstNumber: 35, secondNumber: 7, operation: 'division' },
  { id: 198, question: '42 ÷ 7', firstNumber: 42, secondNumber: 7, operation: 'division' },
  { id: 199, question: '49 ÷ 7', firstNumber: 49, secondNumber: 7, operation: 'division' },
  { id: 200, question: '56 ÷ 7', firstNumber: 56, secondNumber: 7, operation: 'division' },
  { id: 201, question: '63 ÷ 7', firstNumber: 63, secondNumber: 7, operation: 'division' },
  { id: 202, question: '70 ÷ 7', firstNumber: 70, secondNumber: 7, operation: 'division' },
  { id: 203, question: '77 ÷ 7', firstNumber: 77, secondNumber: 7, operation: 'division' },
  { id: 204, question: '84 ÷ 7', firstNumber: 84, secondNumber: 7, operation: 'division' },
  { id: 205, question: '8 ÷ 8', firstNumber: 8, secondNumber: 8, operation: 'division' },
  { id: 206, question: '16 ÷ 8', firstNumber: 16, secondNumber: 8, operation: 'division' },
  { id: 207, question: '24 ÷ 8', firstNumber: 24, secondNumber: 8, operation: 'division' },
  { id: 208, question: '32 ÷ 8', firstNumber: 32, secondNumber: 8, operation: 'division' },
  { id: 209, question: '40 ÷ 8', firstNumber: 40, secondNumber: 8, operation: 'division' },
  { id: 210, question: '48 ÷ 8', firstNumber: 48, secondNumber: 8, operation: 'division' },
  { id: 211, question: '56 ÷ 8', firstNumber: 56, secondNumber: 8, operation: 'division' },
  { id: 212, question: '64 ÷ 8', firstNumber: 64, secondNumber: 8, operation: 'division' },
  { id: 213, question: '72 ÷ 8', firstNumber: 72, secondNumber: 8, operation: 'division' },
  { id: 214, question: '80 ÷ 8', firstNumber: 80, secondNumber: 8, operation: 'division' },
  { id: 215, question: '88 ÷ 8', firstNumber: 88, secondNumber: 8, operation: 'division' },
  { id: 216, question: '96 ÷ 8', firstNumber: 96, secondNumber: 8, operation: 'division' },
  { id: 217, question: '9 ÷ 9', firstNumber: 9, secondNumber: 9, operation: 'division' },
  { id: 218, question: '18 ÷ 9', firstNumber: 18, secondNumber: 9, operation: 'division' },
  { id: 219, question: '27 ÷ 9', firstNumber: 27, secondNumber: 9, operation: 'division' },
  { id: 220, question: '36 ÷ 9', firstNumber: 36, secondNumber: 9, operation: 'division' },
  { id: 221, question: '45 ÷ 9', firstNumber: 45, secondNumber: 9, operation: 'division' },
  { id: 222, question: '54 ÷ 9', firstNumber: 54, secondNumber: 9, operation: 'division' },
  { id: 223, question: '63 ÷ 9', firstNumber: 63, secondNumber: 9, operation: 'division' },
  { id: 224, question: '72 ÷ 9', firstNumber: 72, secondNumber: 9, operation: 'division' },
  { id: 225, question: '81 ÷ 9', firstNumber: 81, secondNumber: 9, operation: 'division' },
  { id: 226, question: '90 ÷ 9', firstNumber: 90, secondNumber: 9, operation: 'division' },
  { id: 227, question: '99 ÷ 9', firstNumber: 99, secondNumber: 9, operation: 'division' },
  { id: 228, question: '108 ÷ 9', firstNumber: 108, secondNumber: 9, operation: 'division' },
  { id: 229, question: '10 ÷ 10', firstNumber: 10, secondNumber: 10, operation: 'division' },
  { id: 230, question: '20 ÷ 10', firstNumber: 20, secondNumber: 10, operation: 'division' },
  { id: 231, question: '30 ÷ 10', firstNumber: 30, secondNumber: 10, operation: 'division' },
  { id: 232, question: '40 ÷ 10', firstNumber: 40, secondNumber: 10, operation: 'division' },
  { id: 233, question: '50 ÷ 10', firstNumber: 50, secondNumber: 10, operation: 'division' },
  { id: 234, question: '60 ÷ 10', firstNumber: 60, secondNumber: 10, operation: 'division' },
  { id: 235, question: '70 ÷ 10', firstNumber: 70, secondNumber: 10, operation: 'division' },
  { id: 236, question: '80 ÷ 10', firstNumber: 80, secondNumber: 10, operation: 'division' },
  { id: 237, question: '90 ÷ 10', firstNumber: 90, secondNumber: 10, operation: 'division' },
  { id: 238, question: '100 ÷ 10', firstNumber: 100, secondNumber: 10, operation: 'division' },
  { id: 239, question: '110 ÷ 10', firstNumber: 110, secondNumber: 10, operation: 'division' },
  { id: 240, question: '120 ÷ 10', firstNumber: 120, secondNumber: 10, operation: 'division' },
  { id: 241, question: '11 ÷ 11', firstNumber: 11, secondNumber: 11, operation: 'division' },
  { id: 242, question: '22 ÷ 11', firstNumber: 22, secondNumber: 11, operation: 'division' },
  { id: 243, question: '33 ÷ 11', firstNumber: 33, secondNumber: 11, operation: 'division' },
  { id: 244, question: '44 ÷ 11', firstNumber: 44, secondNumber: 11, operation: 'division' },
  { id: 245, question: '55 ÷ 11', firstNumber: 55, secondNumber: 11, operation: 'division' },
  { id: 246, question: '66 ÷ 11', firstNumber: 66, secondNumber: 11, operation: 'division' },
  { id: 247, question: '77 ÷ 11', firstNumber: 77, secondNumber: 11, operation: 'division' },
  { id: 248, question: '88 ÷ 11', firstNumber: 88, secondNumber: 11, operation: 'division' },
  { id: 249, question: '99 ÷ 11', firstNumber: 99, secondNumber: 11, operation: 'division' },
  { id: 250, question: '110 ÷ 11', firstNumber: 110, secondNumber: 11, operation: 'division' },
  { id: 251, question: '121 ÷ 11', firstNumber: 121, secondNumber: 11, operation: 'division' },
  { id: 252, question: '132 ÷ 11', firstNumber: 132, secondNumber: 11, operation: 'division' },
  { id: 253, question: '12 ÷ 12', firstNumber: 12, secondNumber: 12, operation: 'division' },
  { id: 254, question: '24 ÷ 12', firstNumber: 24, secondNumber: 12, operation: 'division' },
  { id: 255, question: '36 ÷ 12', firstNumber: 36, secondNumber: 12, operation: 'division' },
  { id: 256, question: '48 ÷ 12', firstNumber: 48, secondNumber: 12, operation: 'division' },
  { id: 257, question: '60 ÷ 12', firstNumber: 60, secondNumber: 12, operation: 'division' },
  { id: 258, question: '72 ÷ 12', firstNumber: 72, secondNumber: 12, operation: 'division' },
  { id: 259, question: '84 ÷ 12', firstNumber: 84, secondNumber: 12, operation: 'division' },
  { id: 260, question: '96 ÷ 12', firstNumber: 96, secondNumber: 12, operation: 'division' },
  { id: 261, question: '108 ÷ 12', firstNumber: 108, secondNumber: 12, operation: 'division' },
  { id: 262, question: '120 ÷ 12', firstNumber: 120, secondNumber: 12, operation: 'division' },
  { id: 263, question: '132 ÷ 12', firstNumber: 132, secondNumber: 12, operation: 'division' },
  { id: 264, question: '144 ÷ 12', firstNumber: 144, secondNumber: 12, operation: 'division' }
]

